import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Users, Briefcase, Layers, Building2, MapPin, ShieldCheck, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MotionReveal } from '../components/strategy/Motion';
import { leadership } from '../data/leadership';
import { companyData } from '../data/mock';

const GROUP_ICONS = {
  group: Users,
  platforms: Layers,
  crosscutting: Briefcase,
};

const Leadership = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="App font-sans bg-white">
      <Header onPartnerClick={() => {}} />

      {/* HERO */}
      <section className="relative pt-32 pb-16 px-6 bg-[#0a1729] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(#2f9e8e 1px, transparent 1px), linear-gradient(90deg, #2f9e8e 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#2f9e8e]/10 blur-3xl pointer-events-none" style={{ animation: 'big-pulse 9s ease-in-out infinite' }} />

        <div className="relative max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8" data-testid="leadership-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#7fd6c9] mb-3">Leadership · Governance · Organisation</div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold leading-[1.05] max-w-4xl">
            The team building the $500B platform.
          </h1>
          <p className="text-lg text-white/70 max-w-3xl mt-6">
            BIG's leadership structure spans group executive, six operating-platform CEOs, and cross-cutting technology and sovereign-partnership leadership. All positions below are filled.
          </p>

          {/* Topline */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {[
              { v: '14', l: 'Executive roles', s: 'Group + platforms + cross-cutting' },
              { v: '7',  l: 'Board committees', s: 'Investment, Risk, Audit & more' },
              { v: '5',  l: 'Regional hubs',   s: 'U.S. · UAE · East · South · West Africa' },
              { v: '125+',l: 'Year-3 headcount', s: 'Excluding portfolio companies' },
            ].map((t, i) => (
              <MotionReveal key={t.l} delay={i * 80} className="bg-white/[0.04] border border-white/10 rounded-xl p-5">
                <div className="text-2xl font-bold text-white">{t.v}</div>
                <div className="text-xs text-white/70 mt-1">{t.l}</div>
                <div className="text-[10px] text-white/40 mt-0.5">{t.s}</div>
              </MotionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* GROUPS */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto space-y-16">
          {leadership.groups.map((g, gi) => {
            const Icon = GROUP_ICONS[g.key] || Users;
            return (
              <div key={g.key}>
                <MotionReveal>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#1a365d] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#7fd6c9]" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#2f9e8e]">Group {gi + 1}</div>
                      <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#1a365d] leading-tight">{g.label}</h2>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm max-w-3xl mb-6 leading-relaxed pl-13 ml-1">{g.blurb}</p>
                </MotionReveal>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {g.members.map((m, mi) => (
                    <MotionReveal key={m.role} delay={mi * 60}>
                      <div className="group bg-[#f6f9fb] hover:bg-white border border-gray-100 hover:border-[#2f9e8e]/40 hover:shadow-lg rounded-xl p-5 transition-all hover:-translate-y-0.5 h-full">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1a365d] to-[#2f9e8e] flex items-center justify-center flex-shrink-0">
                            <Users className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-serif text-base font-bold text-[#1a365d] leading-snug">{m.role}</h3>
                            <div className="inline-block mt-1.5 text-[9px] font-bold tracking-wider uppercase text-[#2f9e8e] bg-[#e8f5f3] px-2 py-0.5 rounded-full">
                              Position filled
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{m.focus}</p>
                      </div>
                    </MotionReveal>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* GOVERNANCE — committees */}
      <section className="py-16 px-6 bg-[#f6f9fb]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10">
            <MotionReveal className="lg:col-span-5">
              <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#2f9e8e] mb-3">Governance Architecture</div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1a365d] leading-tight mb-4">
                Board of 11 directors. Independent oversight from day one.
              </h2>
              <p className="text-gray-600 leading-relaxed">
                A majority of directors will become independent as institutional capital is raised. The Investment Committee operates with authority independent of business-development teams. Every transaction must address strategic fit, downside case, currency risk, sovereign risk, compliance and exit pathway.
              </p>
            </MotionReveal>
            <div className="lg:col-span-7">
              <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#2f9e8e] mb-3">Seven Board Committees</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {leadership.committees.map((c, i) => (
                  <MotionReveal key={c} delay={i * 50}>
                    <div className="bg-white border border-gray-100 rounded-lg p-4 flex items-center gap-3 hover:border-[#2f9e8e]/40 transition-colors">
                      <div className="w-9 h-9 rounded bg-[#e8f5f3] flex items-center justify-center flex-shrink-0">
                        <ShieldCheck className="w-4 h-4 text-[#2f9e8e]" />
                      </div>
                      <span className="text-sm font-medium text-[#1a365d]">{c}</span>
                    </div>
                  </MotionReveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HUBS + Staffing */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#2f9e8e] mb-3">Regional Hubs</div>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#1a365d] leading-tight mb-6">Five offices. One platform.</h2>
              <div className="space-y-2">
                {leadership.hubs.map((h, i) => (
                  <MotionReveal key={h.city} delay={i * 50}>
                    <div className="flex items-center gap-4 p-4 border border-gray-100 rounded-lg hover:bg-[#f6f9fb] transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-[#1a365d] flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-[#7fd6c9]" />
                      </div>
                      <div className="flex-1">
                        <div className="font-serif text-base font-bold text-[#1a365d]">{h.city}</div>
                        <div className="text-sm text-gray-600">{h.role}</div>
                      </div>
                      <MapPin className="w-4 h-4 text-[#2f9e8e]" />
                    </div>
                  </MotionReveal>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Additional country offices are established only when supported by live operating activity.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#c19a3c] mb-3">Staffing Trajectory</div>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#1a365d] leading-tight mb-6">From 35 to 175+ professionals.</h2>
              <div className="space-y-3">
                {leadership.staffing.map((s, i) => (
                  <MotionReveal key={s.stage} delay={i * 100}>
                    <div className="bg-gradient-to-br from-[#fcf5e3] to-white border border-[#c19a3c]/20 rounded-xl p-5">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#c19a3c]">{s.stage}</div>
                      <div className="font-serif text-2xl font-bold text-[#1a365d] mt-1">{s.size}</div>
                      <p className="text-xs text-gray-600 mt-2 leading-relaxed">{s.detail}</p>
                    </div>
                  </MotionReveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#0a1729] text-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#7fd6c9] mb-3">Connect</div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-5">Want to reach our leadership team?</h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-8">
            Introductions, partnership inquiries and institutional dialogue are welcome. Our team routes every message to the right leader.
          </p>
          <a
            href={`mailto:${companyData.contactEmail}?subject=${encodeURIComponent('Leadership inquiry — Berkshire Investment Group')}&body=${encodeURIComponent('Hello,%0D%0A%0D%0AI am writing to connect with the leadership team at berkshire.inc/leadership.%0D%0A%0D%0ATopic:%0D%0A%0D%0ABackground:%0D%0A%0D%0ABest regards,')}`}
            className="inline-flex items-center gap-2 bg-[#2f9e8e] hover:bg-[#268578] px-7 py-4 rounded-lg font-semibold transition-all hover:-translate-y-0.5"
            data-testid="leadership-cta-email"
          >
            <Mail className="w-5 h-5" /> Email leadership@berkshire.inc <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Leadership;
